import React, { useEffect, useState, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import { injectIntl, FormattedMessage } from 'react-intl';
import { Row, Col } from 'react-bootstrap';
import { withRouter, Route, Switch } from 'react-router-dom';
import { Select, Button, Loader } from 'sa-ui';
import queryString from 'query-string';

import * as actions from '../../reportsActions';
import * as detailsActions from '../reportApplicationDetailsActions';

import messages from '../reportApplicationDetailsMessages';
import StudentsGradeTable from '../../general-panel/StudentsGradeTable';
import StudentsComparativeGradeTable from '../../general-panel/StudentsComparativeGradeTable';
import PerformanceStudentsComparative from '../../PerformanceStudentsComparative';
import Chart from '../../ReportBarChart';
import RankingStudentCard from '../../RankingStudentCard';
import LoadMore from '../../LoadMore';
import { promisify } from '../../../app/actions';

import reportMessages from '../../reportsMessages';
import * as helpers from '../../reportHelpers';

import '../../ReportsCommon.scss';

function getRankingLimitFromUrl() {
  const params = queryString.parse(window.location.search.replace('?', ''));
  return params.rankingLimit ? Number(params.rankingLimit) : undefined;
}

const ReportApplicationClassesStudentsContainer = (props) => {
  const [filter, setFilter] = useState(queryString.parse(window.location.search.replace('?', '')));
  const [rankingPage, setRankingPage] = useState(0);
  const [loading, setLoading] = useState(false);

  function buildRankingParams(idCSchool, idESCourse, idKnowledgeArea, idCurricularComponent, page) {
    const rankingLimitFromUrl = getRankingLimitFromUrl();
    return {
      idEvaluation: props.idEvaluation,
      idCSchool,
      idESCourse,
      idKnowledgeArea,
      idCurricularComponent,
      page,
      ...(rankingLimitFromUrl && { rankingLimit: rankingLimitFromUrl }),
    };
  }

  async function loadMoreRanking() {
    const nextPage = rankingPage + 1;
    setRankingPage(nextPage);
    await props.getESGradeByRankingStudent(
      buildRankingParams(
        filter.idCSchool,
        filter.idESCourse,
        filter.idKnowledgeArea,
        filter.idCurricularComponent,
        nextPage,
      ),
    );
  }

  useEffect(() => {
    setLoading(true);
    props.getGroups();
  }, []);

  async function selectFilter(idCSchool, idESCourse, idKnowledgeArea, idCurricularComponent) {
    setFilter({ ...filter, idCSchool });
    setLoading(true);

    try {
      //if idKnowledgeArea has comma call getESGradeComparativeByStudent and stop the function
      if (idKnowledgeArea && idKnowledgeArea.includes(',')) {
        // create an array with the idKnowledgeArea called idEvaluations
        const idEvaluations = idKnowledgeArea.split(',');
        await props.getESGradeComparativeByStudent({
          idEvaluation: idEvaluations,
          idCSchool,
          idESCourse,
          idCurricularComponent,
        });
        return;
      }

      if (idCSchool || idKnowledgeArea || idCurricularComponent) {
        await props.getESGradeByGroup({
          idEvaluation: props.idEvaluation,
          idCSchool,
          idKnowledgeArea,
          idCurricularComponent,
        });
      }

      await props.getEvaluation({ idEvaluation: props.idEvaluation, idCSchool });
      await props.getESGradeByStudent({
        idEvaluation: props.idEvaluation,
        idCSchool,
        idESCourse,
        idKnowledgeArea,
        idCurricularComponent,
      });

      setRankingPage(0);
      await props.getESGradeByRankingStudent(
        buildRankingParams(idCSchool, idESCourse, idKnowledgeArea, idCurricularComponent, 0),
      );

      await props.getFiltersLevelApplicationReport({
        idEvaluation: props.idEvaluation,
        idCSchool,
        idKnowledgeArea,
      });
    } finally {
      setLoading(false);
    }
  }

  async function FiltersLevelApplicationReport(idEvaluation) {
    setLoading(true);
    try {
      await props.getFiltersLevelApplicationReport({
        idEvaluation,
      });
    } finally {
      // setLoading(false);
    }
  }

  useEffect(() => {
    FiltersLevelApplicationReport(props.idEvaluation);
  }, [props.idEvaluation]);

  useEffect(() => {
    selectFilter(
      filter.idCSchool,
      filter.idESCourse,
      filter.idKnowledgeArea,
      filter.idCurricularComponent,
    );
  }, [filter.idCSchool, filter.idESCourse, filter.idKnowledgeArea, filter.idCurricularComponent]);

  useEffect(() => {
    if (props.filtersLevelApplication && props.filtersLevelApplication.schools) {
      if (props.filtersLevelApplication.schools.length === 1) {
        setFilter({
          ...filter,
          idCSchool: props.filtersLevelApplication.schools[0].idCSchool,
        });
      }
    }
  }, [props.filtersLevelApplication]);

  function handleChangeSchool(event) {
    setFilter({
      ...filter,
      idCSchool: event.target.value,
      idESCourse: undefined,
      idKnowledgeArea: undefined,
      idCurricularComponent: undefined,
    });
  }

  function handleChangeCourse(event) {
    setFilter({
      ...filter,
      idESCourse: event.target.value,
    });
  }

  function handleChangeCatalogingKnowledge(event) {
    setFilter({
      ...filter,
      idKnowledgeArea: event.target.value,
      idCurricularComponent: undefined,
    });
  }

  function handleChangeCatalogingCurricularComponent(event) {
    setFilter({
      ...filter,
      idCurricularComponent: event.target.value,
    });
  }

  function clearFilters() {
    setFilter({
      idCSchool: undefined,
      idESCourse: undefined,
      idKnowledgeArea: undefined,
      idCurricularComponent: undefined,
    });
  }

  const schoolOptions = helpers.getFASchoolOptions(props.filtersLevelApplication.schools);
  const classesOptions = helpers.getFAClassesOptions(props.filtersLevelApplication.courses);
  const knowledgeAreaOptions = helpers.getFAKnowledgeAreasOptions(
    props.filtersLevelApplication.knowledgeAreas,
  );
  const curricularComponentOptions = helpers.getFACurricularComponentOptions(
    props.filtersLevelApplication.curricularComponents,
  );

  useImperativeHandle(props.forwardRef, () => ({
    getFilter: () => filter,
  }));

  useEffect(() => {
    if (props.gradeByGroup && props.ranking && props.ranking.content && props.ranking.content.length > 0 && props.evaluation.schools && props.gradeByStudent) {
      setTimeout(() => window.dispatchEvent(new CustomEvent('app-ready')));
    }
  }, [props.gradeByGroup, props.evaluation.schools, props.gradeByStudent, props.ranking]);

  return (
    <div>
      <Loader show={loading} />
      <h6 className="filter subtitle mt-3">
        <i className="pe-7s-filter" />
        <FormattedMessage {...messages.filters} />
        <FormattedMessage {...messages.clear}>
          {(txt) => (
            <button className="btn btn-primary btn-sm ml-3" onClick={clearFilters}>
              {txt}
            </button>
          )}
        </FormattedMessage>
      </h6>
      <Row className="align-items-center">
        <Col md={6}>
          <Select
            containerClassName="mt-0"
            nullable
            name="idCSchool"
            value={filter.idCSchool}
            onChange={handleChangeSchool}
            options={schoolOptions}
            placeholder={props.intl.formatMessage(reportMessages.school)}
          />
        </Col>
        <Col md={6}>
          <Select
            containerClassName="mt-0"
            disabled={!filter.idCSchool}
            nullable
            name="idESCourse"
            value={filter.idESCourse}
            onChange={handleChangeCourse}
            options={classesOptions}
            placeholder={props.intl.formatMessage(reportMessages.class)}
          />
        </Col>
      </Row>
      <Row className="align-items-center mt-3 mb-3">
        <Col md={6}>
          <Select
            containerClassName="mt-0"
            disabled={!filter.idCSchool}
            nullable
            name="handleChangeCatalogingKnowledge"
            onChange={handleChangeCatalogingKnowledge}
            value={filter.idKnowledgeArea}
            options={knowledgeAreaOptions}
            placeholder={props.intl.formatMessage(messages.knowledgeArea)}
          />
        </Col>
        <Col md={6}>
          <Select
            containerClassName="mt-0"
            disabled={!filter.idKnowledgeArea}
            nullable
            name="idCurricularComponent"
            value={filter.idCurricularComponent}
            onChange={handleChangeCatalogingCurricularComponent}
            options={curricularComponentOptions}
            placeholder={props.intl.formatMessage(messages.curricularComponent)}
          />
        </Col>
      </Row>
      <div id="divToPDF" className="divToPDF">
        <Row>
          <Col>
            {props.gradeByGroup && filter.idCSchool ? (
              <Chart
                title={props.intl.formatMessage(messages.generalPerformanceByClass)}
                titleColor="#ea654b"
                schools={props.gradeByGroup.group}
                averageSchools={props.gradeByGroup.avgHit}
                isGradeGroupAvgView
              />
            ) : (
              ''
            )}
          </Col>
        </Row>
        <Row>
          <Col>
            {props.evaluation.schools &&
            props.gradeByStudent &&
            props.gradeByStudent.students &&
            filter.idCSchool ? (
              <PerformanceStudentsComparative
                title={props.intl.formatMessage(reportMessages.performanceStudentsComparativeTitle)}
                itens={props.gradeByStudent}
                school={props.evaluation.schools[0]}
                squareSize={{ maxWidth: '12.5%', flex: '0 0 12.5%' }}
                size={60}
              />
            ) : (
              ''
            )}
          </Col>
        </Row>
        <Row>
          <Col>
            {props.evaluation.schools &&
            props.gradeByStudent &&
            props.gradeByStudent.students &&
            !props.hasComparative &&
            filter.idCSchool ? (
              <StudentsGradeTable itens={props.gradeByStudent} />
            ) : (
              ''
            )}
            {props.evaluation.schools &&
            props.gradeComparativeByStudent &&
            props.gradeComparativeByStudent.students &&
            props.hasComparative &&
            filter.idCSchool ? (
              <StudentsComparativeGradeTable itens={props.gradeComparativeByStudent} />
            ) : (
              ''
            )}
          </Col>
        </Row>
        {props.ranking && props.ranking.content && props.ranking.content.length > 0 && filter.idCSchool && (
          <div>
            <div>
              <h5 className="title">{props.intl.formatMessage(messages.studentsRanking)}</h5>
            </div>
            <div>
              <Row>
                <Col>
                  {props.ranking.content.map((student) => (
                    <Row className="align-items-center" key={student.position}>
                      <Col style={{ width: 40 }} md="auto">
                        {student.position}º
                      </Col>
                      <Col>
                        <RankingStudentCard
                          initials={student.studentInitials}
                          percentage={student.percentHits}
                          studentName={student.studentName}
                          studentInfo={`${student.courseName} | ${student.schoolName}`}
                          index={student.position}
                        />
                      </Col>
                    </Row>
                  ))}
                  <hr className="my-3" />
                  {rankingPage + 1 < props.ranking.totalpages && (
                    <LoadMore onClick={loadMoreRanking} />
                  )}
                </Col>
              </Row>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

ReportApplicationClassesStudentsContainer.propTypes = {};

const mapStateToProps = ({ reports, reportApplicationDetails, app }, { match }) => ({
  ...reports,
  ...reportApplicationDetails,
  ...match.params,
  permission: app.permission,
});

export default compose(
  injectIntl,
  withRouter,
  connect(mapStateToProps, promisify({ ...actions, ...detailsActions })),
)(ReportApplicationClassesStudentsContainer);
